import { jwtDecode } from 'jwt-decode';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import initApi from '@/api/initApi.ts';
import { AuthenticationApi, UsersApi } from '@/clients/v1/index.ts';
import { Configuration as ConfigurationV1 } from '../clients/v1/configuration.ts';
import { Configuration as ConfigurationV2 } from '../clients/v2/configuration.ts';

import { useOrganizationsStore } from './v2/OrganizationStore.ts';

import { handleApiError } from '@/api/handleApiError.ts';
import { captureError } from '@/composables/Sentry.ts';
import {
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
  setStoredUser,
} from '@/composables/SessionStorage.ts';

import {
  type AuthToken as AuthTokenV1,
  type AuthenticationApiCreateApiTokenAuthApiPostRequest,
  type AuthenticationApiDeleteApiTokenAuthApiDeleteRequest,
  type Token as TokenV1,
  type UsersApiPatchUsersMeUsersMePatchRequest,
} from '@/clients/v1/index.ts';
import { AuthApi, type MfaTotpRegistration } from '@/clients/v2/index.ts';
import type { OrganizationUser } from '../clients/v1/index.ts';
import type { AxiosError, AxiosResponse } from 'axios';

const baseUrl = `${import.meta.env.VITE_API_URL}/api`;
const config = new ConfigurationV1();
config.basePath = baseUrl;
config.accessToken = getAccessToken() ?? '';

const baseUrlV2 = `${import.meta.env.VITE_API_URL}/api/v2`;
const configV2 = new ConfigurationV2();
configV2.basePath = baseUrlV2;
configV2.accessToken = getAccessToken() ?? '';

const authApi = new AuthenticationApi(config);
const authApiV2 = new AuthApi(configV2);
const usersApi = new UsersApi(config);

// Cross-tab token sync. localStorage reads are eventually-consistent across
// renderer processes in Chrome; broadcasting refreshed tokens directly avoids
// a sibling tab reading a stale value after acquiring the refresh lock.
type AuthBroadcast =
  { type: 'tokens'; accessToken: string; refreshToken?: string | null } | { type: 'logout' };
const authChannel: BroadcastChannel | null =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('modulos-auth') : null;

if (authChannel) {
  authChannel.onmessage = (event: MessageEvent<AuthBroadcast>) => {
    if (event.data?.type === 'tokens') {
      setAccessToken(event.data.accessToken);
      config.accessToken = event.data.accessToken;
      configV2.accessToken = event.data.accessToken;
      if (event.data.refreshToken) {
        setRefreshToken(event.data.refreshToken);
      }
      console.debug('[auth] received broadcast tokens', {
        access: event.data.accessToken.slice(0, 8),
        refresh: event.data.refreshToken?.slice(0, 8) ?? '<none>',
      });
      return;
    }
    if (event.data?.type === 'logout') {
      config.accessToken = '';
      configV2.accessToken = '';
      // The tab that logged out cleared only its own sessionStorage. Best-effort: the redirect must run.
      try {
        sessionStorage.clear();
      } catch {
        // sessionStorage may be unavailable
      }
      console.debug('[auth] received broadcast logout — navigating to /login');
      window.location.assign(`${window.location.origin}/login`);
      return;
    }
  };
}

export const broadcastLogout = () => {
  authChannel?.postMessage({ type: 'logout' } satisfies AuthBroadcast);
};

export const useAuthStore = defineStore('auth', () => {
  const user = ref<null | OrganizationUser>(null);
  const allTokens = ref<null | TokenV1[]>(null);
  const tokenData = ref<null | AuthTokenV1>(null);
  const totpEnabled = ref<boolean | null>(null);
  const totpRegistration = ref<null | MfaTotpRegistration>(null);

  const getMeAndOrgConfig = async () => {
    const organizationsStore = useOrganizationsStore();

    try {
      const response = await usersApi.getUsersMeUsersMeGet();

      if (!response || !response.data) {
        captureError(new Error('Failed to fetch user data'), {
          message: 'Store: AuthStore, Function: getMeAndOrgConfig',
          data: response ? response.data : response,
        });

        return;
      }

      user.value = response.data;

      if (user.value) {
        setStoredUser(user.value);
      }

      if (user.value && user.value.organization) {
        await organizationsStore.getOrganizationConfig(
          {
            organizationId: user.value.organization.id,
          },
          true,
        );
      }
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateMe = async (params: UsersApiPatchUsersMeUsersMePatchRequest) => {
    try {
      await usersApi.patchUsersMeUsersMePatch(params);
      await getMeAndOrgConfig();
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getTokens = async () => {
    try {
      const response: AxiosResponse = await authApi.getApiTokensAuthApiGet();
      allTokens.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateTermsAccepted = async (params: UsersApiPatchUsersMeUsersMePatchRequest) => {
    try {
      await usersApi.patchUsersMeUsersMePatch(params);

      await getMeAndOrgConfig();
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const createToken = async (params: AuthenticationApiCreateApiTokenAuthApiPostRequest) => {
    try {
      const response: AxiosResponse = await authApi.createApiTokenAuthApiPost(params);

      tokenData.value = response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const deleteToken = async (params: AuthenticationApiDeleteApiTokenAuthApiDeleteRequest) => {
    try {
      await authApi.deleteApiTokenAuthApiDelete(params);

      await getTokens();
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const syncApiAccessToken = async () => {
    const accessToken = await getAccessTokenFromRefreshToken();
    if (!accessToken) return false;

    config.accessToken = accessToken;
    configV2.accessToken = accessToken;
    return true;
  };

  const getTotpStatus = async () => {
    try {
      if (!(await syncApiAccessToken())) return;
      const response = await authApiV2.getTotpStatus();

      totpEnabled.value = response.data.enabled;
      if (response.data.enabled) {
        totpRegistration.value = null;
      }
      return response.data.enabled;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const registerTotp = async () => {
    try {
      if (!(await syncApiAccessToken())) return;
      const response = await authApiV2.registerTotp();

      totpRegistration.value = response.data;
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const verifyTotp = async (code: string) => {
    try {
      if (!(await syncApiAccessToken())) return;
      const response = await authApiV2.verifyTotp({
        mfaTotpVerification: { code },
      });

      totpEnabled.value = response.data.enabled;
      totpRegistration.value = null;
      return response.data.enabled;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const removeTotp = async () => {
    try {
      if (!(await syncApiAccessToken())) return;
      const response = await authApiV2.removeTotp();

      totpEnabled.value = response.data.enabled;
      totpRegistration.value = null;
      return response.data.enabled;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const isTokenFresh = (token: string | null): token is string => {
    if (!token) return false;
    try {
      const { exp } = jwtDecode<{ exp: number }>(token);
      return exp > Date.now() / 1000 + 30;
    } catch {
      return false;
    }
  };

  const tokenPrefix = (token: string | null | undefined): string =>
    token ? token.slice(0, 8) : '<none>';

  const performRefresh = async (): Promise<string | undefined> => {
    const current = getAccessToken();
    if (isTokenFresh(current)) {
      config.accessToken = current;
      configV2.accessToken = current;
      console.debug('[auth] refresh skipped — cached access token still fresh', {
        access: tokenPrefix(current),
      });
      return current;
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      console.debug('[auth] refresh aborted — no refresh token in storage');
      return;
    }

    console.debug('[auth] refresh calling /auth/refresh', {
      refresh: tokenPrefix(refreshToken),
    });

    try {
      const response = await authApi.refreshSessionTokenAuthRefreshPost({
        grantType: 'refresh_token',
        refreshToken,
      });

      const data = response.data;
      if (data?.access_token) {
        setAccessToken(data.access_token);
        config.accessToken = data.access_token;
        configV2.accessToken = data.access_token;

        if (data.refresh_token) {
          setRefreshToken(data.refresh_token);
        }

        authChannel?.postMessage({
          type: 'tokens',
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
        } satisfies AuthBroadcast);

        console.debug('[auth] refresh succeeded', {
          sentRefresh: tokenPrefix(refreshToken),
          newAccess: tokenPrefix(data.access_token),
          newRefresh: tokenPrefix(data.refresh_token),
        });

        return data.access_token;
      } else {
        console.error('Invalid response format');
      }
    } catch (error) {
      console.debug('[auth] refresh failed', {
        sentRefresh: tokenPrefix(refreshToken),
        status: (error as AxiosError)?.response?.status,
      });
      handleApiError(error as AxiosError);
    }
  };

  const getAccessTokenFromRefreshToken = async (): Promise<string | undefined> => {
    if (typeof navigator !== 'undefined' && 'locks' in navigator) {
      const requestedAt = performance.now();
      console.debug('[auth] refresh requesting lock', { origin: location.origin });
      return navigator.locks.request('modulos-token-refresh', { mode: 'exclusive' }, async () => {
        const acquiredAt = performance.now();
        console.debug('[auth] refresh acquired lock', {
          waitedMs: Math.round(acquiredAt - requestedAt),
        });
        // Drain any pending BroadcastChannel / storage events from a sibling
        // tab's just-completed refresh before reading localStorage.
        await new Promise((resolve) => setTimeout(resolve, 0));
        try {
          return await performRefresh();
        } finally {
          console.debug('[auth] refresh releasing lock', {
            heldMs: Math.round(performance.now() - acquiredAt),
          });
        }
      });
    }
    console.debug('[auth] refresh running WITHOUT lock — navigator.locks unavailable');
    return performRefresh();
  };

  const userLastAccess = async () => {
    try {
      const usersApi = await initApi(UsersApi);
      await usersApi?.patchUsersMeLastAccessUsersMeLastAccessPatch();
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const exchangeAuthCode = async (code: string) => {
    try {
      const response = await authApi.tokenExchangeAuthTokenExchangePost({ code });
      const data = response.data;
      if (data?.access_token) {
        setTokens(data.access_token, data.refresh_token ?? undefined);
        return data;
      }
    } catch (error) {
      captureError(error, {
        message: 'Store: AuthStore, Function: exchangeAuthCode',
      });
    }
    return null;
  };

  const setTokens = (accessToken: string, refreshToken?: string) => {
    setAccessToken(accessToken);
    config.accessToken = accessToken;
    configV2.accessToken = accessToken;
    if (refreshToken) {
      setRefreshToken(refreshToken);
    }
  };

  const logoutFromIdp = async (): Promise<string | null> => {
    try {
      const response = await authApi.logoutAuthLogoutPost();
      return response.data?.redirect_url ?? null;
    } catch {
      return null;
    }
  };

  const resetStore = () => {
    user.value = null;
    allTokens.value = null;
    tokenData.value = null;
    totpEnabled.value = null;
    totpRegistration.value = null;
    config.accessToken = '';
    configV2.accessToken = '';
  };

  return {
    getMeAndOrgConfig,
    updateMe,
    user,
    allTokens,
    getTokens,
    createToken,
    tokenData,
    deleteToken,
    totpEnabled,
    totpRegistration,
    getTotpStatus,
    registerTotp,
    verifyTotp,
    removeTotp,
    getAccessTokenFromRefreshToken,
    userLastAccess,
    updateTermsAccepted,
    exchangeAuthCode,
    setTokens,
    logoutFromIdp,
    resetStore,
  };
});
